import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingDown as TrendingDownIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { ThemeContext } from './context/ThemeContext';
import api from '../api/api';
import AddRentalPointDialog from './dialogs/AddRentalPointDialog';
import { calendarDate } from '../../utils/calendarDate';

// Ключ для сохранения состояния таблицы в localStorage
const TABLE_STATE_KEY = "table_state_expenses";

// Одинаковые календарные границы для списка, аналитики и выгрузки.
const expenseDateRange = (period, from, to) => {
  if (period === 'all') return {};
  if (period === 'custom' && from && to) return { start_date: from, end_date: to };
  const now = new Date();
  let start = now;
  let end = now;
  if (period === 'month' || period === 'custom') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    if (period === 'month') end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (period === 'year') {
    start = new Date(now.getFullYear(), 0, 1);
    end = new Date(now.getFullYear(), 11, 31);
  }
  return { start_date: calendarDate(start), end_date: calendarDate(end) };
};

// Функция для загрузки сохраненного состояния таблицы
const loadTableState = () => {
  try {
    const saved = localStorage.getItem(TABLE_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Ошибка загрузки состояния таблицы:", error);
  }
  return null;
};

// Функция для сохранения состояния таблицы
const saveTableState = (state) => {
  try {
    localStorage.setItem(TABLE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Ошибка сохранения состояния таблицы:", error);
  }
};

const Expenses = () => {
  // Загружаем сохраненное состояние
  const savedState = loadTableState();
  
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [period, setPeriod] = useState('month'); // 'day', 'month', 'year', 'all', 'custom'
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedRentalPoint, setSelectedRentalPoint] = useState('all');
  const [rentalPoints, setRentalPoints] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  
  // Пагинация (с восстановлением из localStorage)
  const [page, setPage] = useState(savedState?.page || 1);
  const [pageSize, setPageSize] = useState(savedState?.pageSize || 20);
  const [totalExpenses, setTotalExpenses] = useState(0);
  
  // Форма добавления расхода
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newExpense, setNewExpense] = useState({
    rental_point_id: '',
    name: '',
    amount: '',
    category: '',
    description: '',
    expense_date: calendarDate(new Date()) // Текущая дата в формате YYYY-MM-DD
  });

  // Форма редактирования расхода
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editExpense, setEditExpense] = useState({
    rental_point_id: '',
    name: '',
    amount: '',
    category: '',
    description: '',
    expense_date: ''
  });

  // Статистика
  const [expenseStats, setExpenseStats] = useState({
    totalExpenses: 0,
    expensesByCategory: []
  });

  // Диалог создания точки аренды
  const [openAddRentalPointDialog, setOpenAddRentalPointDialog] = useState(false);

  // Состояния для импорта
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Диалог создания категории
  const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Сохраняем состояние таблицы при изменении
  useEffect(() => {
    saveTableState({ page, pageSize });
  }, [page, pageSize]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Вызываем fetchExpenses только после того, как категории загружены
    if (expenseCategories.length > 0) {
      fetchExpenses();
    }
  }, [period, customDateFrom, customDateTo, selectedRentalPoint, expenseCategories, page, pageSize]);

  const fetchInitialData = async () => {
    try {
      // Загружаем rental points
      const rentalPointsResponse = await api.get('/rental-points/').catch(() => ({ data: [] }));
      setRentalPoints(rentalPointsResponse.data || []);
      
      // Загружаем категории расходов из API
      await fetchExpenseCategories();
    } catch (error) {
      console.error('Ошибка загрузки начальных данных:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await api.get('/expenses/categories');
      const categories = response.data || [];
      
      // Если категорий нет - создаём дефолтные
      if (categories.length === 0) {
        console.log('⚠️ Категории расходов не найдены, используем дефолтные');
        setExpenseCategories([
          { id: 'office_rent', code: 'office_rent', name: 'Аренда офиса' },
          { id: 'utilities', code: 'utilities', name: 'Коммунальные услуги' },
          { id: 'marketing', code: 'marketing', name: 'Маркетинг' },
          { id: 'salary', code: 'salary', name: 'Зарплата' },
          { id: 'maintenance', code: 'maintenance', name: 'Обслуживание' },
          { id: 'repair', code: 'repair', name: 'Ремонт' },
          { id: 'insurance', code: 'insurance', name: 'Страхование' },
          { id: 'other', code: 'other', name: 'Прочие расходы' }
        ]);
      } else {
        // Преобразуем в нужный формат (id совпадает с code для совместимости)
        const formatted = categories.map(cat => ({
          id: cat.code,  // Для совместимости с текущим кодом
          code: cat.code,
          name: cat.name,
          dbId: cat.id   // Настоящий ID из БД
        }));
        setExpenseCategories(formatted);
      }
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
      // Фоллбек на дефолтные категории
      setExpenseCategories([
        { id: 'office_rent', code: 'office_rent', name: 'Аренда офиса' },
        { id: 'utilities', code: 'utilities', name: 'Коммунальные услуги' },
        { id: 'marketing', code: 'marketing', name: 'Маркетинг' },
        { id: 'salary', code: 'salary', name: 'Зарплата' },
        { id: 'maintenance', code: 'maintenance', name: 'Обслуживание' },
        { id: 'repair', code: 'repair', name: 'Ремонт' },
        { id: 'insurance', code: 'insurance', name: 'Страхование' },
        { id: 'other', code: 'other', name: 'Прочие расходы' }
      ]);
    }
  };

  const expenseFilters = () => {
    const params = new URLSearchParams(expenseDateRange(period, customDateFrom, customDateTo));
    if (selectedRentalPoint !== 'all') params.set('rental_point_id', selectedRentalPoint);
    return params;
  };

  const fetchExpenses = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const filters = expenseFilters();
      const params = new URLSearchParams(filters);
      params.set('page', page);
      params.set('limit', pageSize);
      const [response, summary] = await Promise.all([
        api.get(`/expenses/?${params.toString()}`),
        api.get(`/expenses/analytics/summary?${filters.toString()}`)
      ]);
      const fetchedExpenses = response.data.items || response.data || [];
      setTotalExpenses(response.data.total ?? fetchedExpenses.length);
      setExpenses(fetchedExpenses);
      setExpenseStats({
        totalExpenses: summary.data.total_amount,
        expensesByCategory: summary.data.categories.map((row, index) => ({
          name: getCategoryName(row.category),
          value: Number(row.total),
          color: getColorForCategory(index)
        })).filter(row => row.value > 0)
      });
    } catch (error) {
      console.error('Ошибка загрузки расходов:', error);
      setLoadError('Не удалось загрузить расходы. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = () => {
    setNewExpense({
      rental_point_id: selectedRentalPoint === 'all' ? '' : selectedRentalPoint,
      name: '',
      amount: '',
      category: '',
      description: '',
      expense_date: calendarDate(new Date()) // Текущая дата в формате YYYY-MM-DD
    });
    setOpenAddDialog(true);
  };

  const handleSaveExpense = async () => {
    try {
      // Валидация
      if (!newExpense.name || !newExpense.amount || !newExpense.category || !newExpense.expense_date) {
        alert('Заполните все обязательные поля (название, сумма, категория, дата)');
        return;
      }

      // Подготавливаем данные для отправки - заменяем пустые строки на null
      const expenseToSend = {
        ...newExpense,
        rental_point_id: newExpense.rental_point_id || null, // Если пустая строка, то null
        description: newExpense.description.trim() || null // Если пустая строка или только пробелы, то null
      };
      
      // Отправляем POST запрос к API
      const response = await api.post('/expenses/', expenseToSend);
      
      // Очищаем форму
      setNewExpense({
        name: '',
        amount: '',
        category: '',
        description: '',
        expense_date: calendarDate(new Date()),
        rental_point_id: ''
      });
      
      setOpenAddDialog(false);
      fetchExpenses(); // Перезагружаем список
      
    } catch (error) {
      console.error('Ошибка сохранения расхода:', error);
      alert('Ошибка при сохранении расхода: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Транслитерация русского в латиницу для кода категории
  const transliterate = (text) => {
    const map = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
      'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
      'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
      'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
      'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
      ' ': '_'
    };
    
    return text
      .toLowerCase()
      .split('')
      .map(char => map[char] || char)
      .join('')
      .replace(/[^a-z0-9_]/g, '');
  };

  const handleCreateCategory = async () => {
    try {
      if (!newCategoryName.trim()) {
        alert('Введите название категории');
        return;
      }

      setCreatingCategory(true);
      
      // Автоматически генерируем код из названия
      const code = transliterate(newCategoryName.trim());
      
      await api.post('/expenses/categories', {
        name: newCategoryName.trim(),
        code: code
      });

      // Перезагружаем категории
      await fetchExpenseCategories();
      
      // Закрываем диалог и очищаем форму
      setOpenAddCategoryDialog(false);
      setNewCategoryName('');
      
    } catch (error) {
      console.error('Ошибка создания категории:', error);
      alert('Ошибка: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditExpense({
      rental_point_id: expense.rental_point_id || '',
      name: expense.name,
      amount: expense.amount.toString(),
      category: expense.category,
      description: expense.description || '',
      expense_date: expense.expense_date
    });
    setOpenEditDialog(true);
  };

  const handleUpdateExpense = async () => {
    try {
      // Валидация
      if (!editExpense.name || !editExpense.amount || !editExpense.category || !editExpense.expense_date) {
        alert('Заполните все обязательные поля (название, сумма, категория, дата)');
        return;
      }

      // Подготавливаем данные для отправки
      const expenseToSend = {
        ...editExpense,
        rental_point_id: editExpense.rental_point_id || null,
        description: editExpense.description.trim() || null
      };
      
      // Отправляем PUT запрос к API
      const response = await api.put(`/expenses/${editingExpense.id}`, expenseToSend);
      
      setOpenEditDialog(false);
      setEditingExpense(null);
      fetchExpenses(); // Перезагружаем список
      
    } catch (error) {
      console.error('Ошибка обновления расхода:', error);
      alert('Ошибка при обновлении расхода: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (window.confirm(`Вы уверены, что хотите удалить расход "${expense.name}"?`)) {
      try {
        await api.delete(`/expenses/${expense.id}`);
        
        fetchExpenses(); // Перезагружаем список
        
      } catch (error) {
        console.error('Ошибка удаления расхода:', error);
        alert('Ошибка при удалении расхода: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // Обработчик экспорта расходов
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = expenseFilters();

      const response = await api.get(`/expenses/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'expenses_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('Экспорт успешно завершен!');
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      alert('Ошибка при экспорте расходов');
    } finally {
      setExporting(false);
    }
  };

  // Обработчик импорта расходов
  const handleImport = async () => {
    if (!importFile) {
      alert('Выберите файл для импорта');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await api.post('/expenses/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      alert(
        `Импортировано: ${response.data.imported}, Пропущено: ${response.data.skipped}`
      );

      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Ошибки импорта:', response.data.errors);
        alert('Некоторые записи не были импортированы. Проверьте консоль для деталей.');
      }

      setImportDialog(false);
      setImportFile(null);
      fetchExpenses();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      alert(
        error.response?.data?.detail || 'Ошибка при импорте расходов'
      );
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/expenses/import/template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_expenses.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('Шаблон скачан');
    } catch (error) {
      console.error('Ошибка скачивания шаблона:', error);
      alert('Ошибка при скачивании шаблона');
    }
  };

  const getRandomColor = () => {
    const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getColorForCategory = (index) => {
    // Массив контрастных цветов, которые хорошо различимы
    const colors = [
      '#FF6B6B', // Красный
      '#4ECDC4', // Бирюзовый  
      '#45B7D1', // Синий
      '#96CEB4', // Зеленый
      '#FFEAA7', // Желтый
      '#DDA0DD', // Сиреневый
      '#98D8C8', // Мятный
      '#F7DC6F', // Золотой
      '#BB8FCE', // Фиолетовый
      '#85C1E9', // Голубой
      '#F8C471', // Оранжевый
      '#82E0AA'  // Салатовый
    ];
    
    // Возвращаем цвет по индексу, если индекс больше количества цветов - начинаем сначала
    return colors[index % colors.length];
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(value);
  };

  const getCategoryName = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const getRentalPointName = (rentalPointId) => {
    const point = rentalPoints.find(point => point.id === parseInt(rentalPointId));
    return point ? point.name : `Точка #${rentalPointId}`;
  };

  const handleRentalPointCreated = (newRentalPoint) => {
    // Обновляем список точек аренды
    setRentalPoints(prev => [...prev, newRentalPoint]);
    // Автоматически выбираем новую точку аренды
    setSelectedRentalPoint(newRentalPoint.id.toString());
  };

  return (
    <Box sx={{ padding: 3 }}>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {/* Заголовок */}
      <Box className="erp-section-heading" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          💸 Управление расходами
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            color="primary"
          >
            {exporting ? 'Экспорт...' : 'Экспорт'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialog(true)}
            color="primary"
          >
            Импорт
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddExpense}
            sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' } }}
          >
            Добавить расход
          </Button>
        </Box>
      </Box>

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Выбор Точки аренды */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Точка аренды</InputLabel>
                  <Select
                    value={selectedRentalPoint}
                    onChange={(e) => setSelectedRentalPoint(e.target.value)}
                    label="Точка аренды"
                  >
                    <MenuItem value="all">Все точки</MenuItem>
                    {rentalPoints.length > 0 ? (
                      rentalPoints.map((point) => (
                        <MenuItem key={point.id} value={point.id}>
                          {point.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <em>Точки аренды не созданы</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: '40px', height: '40px' }}
                  onClick={() => setOpenAddRentalPointDialog(true)}
                  title="Создать новую точку аренды"
                >
                  +
                </Button>
              </Box>
            </Grid>

            {/* Период */}
            <Grid item xs={12} sm={6} md={6}>
              <ButtonGroup variant="outlined" size="small">
                <Button 
                  variant={period === 'day' ? 'contained' : 'outlined'}
                  onClick={() => setPeriod('day')}
                >
                  День
                </Button>
                <Button 
                  variant={period === 'month' ? 'contained' : 'outlined'}
                  onClick={() => setPeriod('month')}
                >
                  Месяц
                </Button>
                <Button 
                  variant={period === 'year' ? 'contained' : 'outlined'}
                  onClick={() => setPeriod('year')}
                >
                  Год
                </Button>
                <Button 
                  variant={period === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setPeriod('all')}
                >
                  Все время
                </Button>
                <Button 
                  variant={period === 'custom' ? 'contained' : 'outlined'}
                  onClick={() => setPeriod('custom')}
                >
                  Мои даты
                </Button>
              </ButtonGroup>
            </Grid>

            {/* Поля выбора дат для пользовательского периода */}
            {period === 'custom' && (
              <Grid item xs={12} sm={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="От"
                    type="date"
                    size="small"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="До"
                    type="date"
                    size="small"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Список расходов */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      📋 Список расходов
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      Всего: {totalExpenses} расходов
                    </Typography>
                  </Box>
                  
                  {/* Выбор количества записей */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Показывать:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(e.target.value);
                          setPage(1);
                        }}
                        sx={{ height: 32 }}
                      >
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                
                {expenses.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Дата</TableCell>
                          <TableCell>Название</TableCell>
                          <TableCell>Категория</TableCell>
                          <TableCell>Точка аренды</TableCell>
                          <TableCell align="right">Сумма</TableCell>
                          <TableCell align="center">Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expenses.map((expense) => (
                          <TableRow key={expense.id}>
                            <TableCell>
                              {expense.expense_date ? new Date(expense.expense_date).toLocaleDateString('ru-RU') : '-'}
                            </TableCell>
                            <TableCell>{expense.name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={getCategoryName(expense.category)} 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {expense.rental_point_id ? 
                                getRentalPointName(expense.rental_point_id) : 
                                <em style={{ color: '#999' }}>Без точки</em>
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                              {formatCurrency(expense.amount)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditExpense(expense)}
                                title="Редактировать расход"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteExpense(expense)}
                                title="Удалить расход"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    Расходов за выбранный период пока нет. Добавьте первый расход!
                  </Alert>
                )}
                
                {/* Пагинация */}
                {totalExpenses > pageSize && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
                    <Button
                      variant="outlined"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Назад
                    </Button>
                    <Typography sx={{ alignSelf: 'center' }}>
                      Страница {page} из {Math.ceil(totalExpenses / pageSize)}
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={page >= Math.ceil(totalExpenses / pageSize)}
                      onClick={() => setPage(page + 1)}
                    >
                      Вперед
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Аналитика расходов */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  📊 Аналитика расходов
                </Typography>
                
                {/* Общая сумма */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)', 
                  color: 'white',
                  p: 2,
                  borderRadius: 2,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(expenseStats.totalExpenses)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Общие расходы {
                        period === 'day' ? 'за день' :
                        period === 'month' ? 'за месяц' : 
                        period === 'year' ? 'за год' : 
                        period === 'custom' ? 'за период' : 
                        'за все время'
                      }
                    </Typography>
                  </Box>
                  <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>

                {/* Диаграмма по категориям */}
                {expenseStats.expensesByCategory.length > 0 && 
                 expenseStats.expensesByCategory.some(cat => cat.value > 0) ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={expenseStats.expensesByCategory.filter(cat => cat.value > 0)}
                        cx="50%"
                        cy="45%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={95}
                        fill="#8884d8"
                        dataKey="value"
                        labelStyle={{
                          fill: 'white',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        {expenseStats.expensesByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend 
                        wrapperStyle={{ 
                          paddingTop: '10px',
                          fontSize: '12px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    Расходы по категориям будут отображены после добавления данных
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Диалог добавления расхода */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💸 Добавить новый расход</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <FormControl fullWidth>
                  <InputLabel>Точка аренды</InputLabel>
                  <Select
                    value={newExpense.rental_point_id}
                    onChange={(e) => setNewExpense({ ...newExpense, rental_point_id: e.target.value })}
                    label="Точка аренды"
                  >
                    <MenuItem value="">
                      <em>Без точки аренды</em>
                    </MenuItem>
                    {rentalPoints.length > 0 ? (
                      rentalPoints.map((point) => (
                        <MenuItem key={point.id} value={point.id}>
                          {point.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <em>Точки аренды не созданы</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ minWidth: '56px', height: '56px' }}
                  onClick={() => setOpenAddRentalPointDialog(true)}
                  title="Создать новую точку аренды"
                >
                  +
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название расхода *"
                value={newExpense.name}
                onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                placeholder="Например: Аренда офиса за январь"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Сумма *"
                type="number"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                placeholder="0"
                InputProps={{
                  endAdornment: '₽'
                }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <FormControl fullWidth>
                  <InputLabel>Категория *</InputLabel>
                  <Select
                    value={newExpense.category}
                    onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                    label="Категория *"
                  >
                    {expenseCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ minWidth: '56px', height: '56px' }}
                  onClick={() => setOpenAddCategoryDialog(true)}
                  title="Создать новую категорию"
                >
                  +
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Дата расхода"
                value={newExpense.expense_date}
                onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Выберите дату, когда был произведен расход"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Описание"
                value={newExpense.description}
                onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                placeholder="Дополнительная информация о расходе"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>
            Отмена
          </Button>
          <Button onClick={handleSaveExpense} variant="contained" color="error">
            Сохранить расход
          </Button>
        </DialogActions>
              </Dialog>

        {/* Edit Expense Dialog */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Редактировать расход</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <FormControl fullWidth>
                    <InputLabel>Точка аренды</InputLabel>
                    <Select
                      value={editExpense.rental_point_id}
                      onChange={(e) => setEditExpense({ ...editExpense, rental_point_id: e.target.value })}
                      label="Точка аренды"
                    >
                      <MenuItem value="">
                        <em>Без точки аренды</em>
                      </MenuItem>
                      {rentalPoints.length > 0 ? (
                        rentalPoints.map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <em>Точки аренды не созданы</em>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Название расхода *"
                  value={editExpense.name}
                  onChange={(e) => setEditExpense({ ...editExpense, name: e.target.value })}
                  placeholder="Например: Аренда офиса за январь"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Сумма *"
                  type="number"
                  value={editExpense.amount}
                  onChange={(e) => setEditExpense({ ...editExpense, amount: e.target.value })}
                  placeholder="0"
                  InputProps={{
                    endAdornment: '₽'
                  }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Категория *</InputLabel>
                  <Select
                    value={editExpense.category}
                    onChange={(e) => setEditExpense({ ...editExpense, category: e.target.value })}
                    label="Категория *"
                  >
                    {expenseCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Дата расхода"
                  value={editExpense.expense_date}
                  onChange={(e) => setEditExpense({ ...editExpense, expense_date: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Выберите дату, когда был произведен расход"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Описание"
                  value={editExpense.description}
                  onChange={(e) => setEditExpense({ ...editExpense, description: e.target.value })}
                  placeholder="Дополнительная информация о расходе"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)}>Отмена</Button>
            <Button onClick={handleUpdateExpense} variant="contained" color="primary">
              Сохранить изменения
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог создания точки аренды */}
        <AddRentalPointDialog
          open={openAddRentalPointDialog}
          onClose={() => setOpenAddRentalPointDialog(false)}
          onSuccess={handleRentalPointCreated}
        />

        {/* Диалог импорта расходов */}
        <Dialog
          open={importDialog}
          onClose={() => !importing && setImportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Импорт расходов</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                1. Скачайте шаблон Excel
              </Typography>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ mb: 2 }}
                fullWidth
              >
                Скачать шаблон
              </Button>

              <Typography variant="body2" gutterBottom>
                2. Заполните данные в шаблоне
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                <strong>Обязательные поля:</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', ml: 2 }}>
                • Дата (формат: дд.мм.гггг)<br />
                • Название<br />
                • Сумма<br />
                • Категория
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                <strong>Дополнительные:</strong> Пункт проката, Описание
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                <strong>Категории:</strong> Аренда офиса, Коммунальные услуги, Маркетинг, Зарплата, Обслуживание, Ремонт, Страхование, Прочие расходы
              </Typography>

              <Typography variant="body2" gutterBottom>
                3. Загрузите заполненный файл
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                {importFile ? importFile.name : 'Выбрать файл'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                />
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialog(false)} disabled={importing}>
              Отмена
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={!importFile || importing}
            >
              {importing ? 'Импортирование...' : 'Импортировать'}
            </Button>
          </DialogActions>
        </Dialog>

      {/* Диалог создания категории */}
      <Dialog open={openAddCategoryDialog} onClose={() => setOpenAddCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>➕ Создать категорию расхода</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="Название категории"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Например: Транспорт"
            helperText="Введите название на русском языке"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCategoryDialog(false)}>
            Отмена
          </Button>
          <Button 
            onClick={handleCreateCategory} 
            variant="contained" 
            disabled={creatingCategory || !newCategoryName.trim()}
          >
            {creatingCategory ? 'Создание...' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
